import { observable } from '@legendapp/state';
import { logger } from '@/lib/utils/logger';
import { Session } from 'next-auth';
import { authService } from '@/lib/services';
import type { AuthUser } from '@/lib/models/client.types';
import type {
  AuthCredentials,
  RegisterCredentials,
  LoginResult,
  AuthResponse,
  AuthStatus,
  AuthTokens,
} from '@/types/auth/auth';

// Types
export interface AuthState {
  user: {
    data: AuthUser | null;
    isLoading: boolean;
    error: Error | null;
  };
  tokens: {
    data: AuthTokens | null;
    isRefreshing: boolean;
  };
  status: {
    current: AuthStatus | 'SIGNED_OUT';
    isTransitioning: boolean;
  };
  session: Session | null;
}

// Initial state
const initialState: AuthState = {
  user: {
    data: null,
    isLoading: false,
    error: null,
  },
  tokens: {
    data: null,
    isRefreshing: false,
  },
  status: {
    current: 'SIGNED_OUT',
    isTransitioning: false,
  },
  session: null,
};

// Create the observable store
export const authState = observable<AuthState>(initialState);

// Helper function to convert service user to client user
const mapServiceUserToClientUser = (
  serviceUser: any,
  tokens: AuthTokens
): AuthUser => ({
  id: serviceUser.id,
  email: serviceUser.email,
  role: serviceUser.role,
  name: serviceUser.name || null,
  city: serviceUser.city || 'Unknown',
  language: serviceUser.language || 'fr',
  ageGroup: serviceUser.ageGroup || 'Unknown',
  acceptedPolicies: serviceUser.acceptedPolicies || false,
  userType: serviceUser.userType || 'Unknown',
  providerId: serviceUser.providerId || null,
  bio: serviceUser.bio || null,
  isAdmin: serviceUser.isAdmin || false,
  isSubscribed: serviceUser.isSubscribed || false,
  accessToken: tokens.accessToken,
  idToken: tokens.idToken,
  hasFilledQuestionnaire: serviceUser.hasFilledQuestionnaire || false,
  formAnswerId: serviceUser.formAnswerId || '',
  status: serviceUser.status || 'UNCONFIRMED',
  picture: serviceUser.picture,
  emailVerified: serviceUser.emailVerified,
  createdAt: serviceUser.createdAt,
  updatedAt: serviceUser.updatedAt,
});

// Action creators
export const authActions = {
  // Session management
  setSession: (session: Session | null) => {
    logger.debug('[authStore] Setting session', { session });
    authState.session.set(session);
  },

  // User actions
  setUser: (user: AuthUser | null) => {
    logger.debug('[authStore] Setting user', { user });
    authState.user.data.set(user);
  },

  startLoading: () => {
    logger.debug('[authStore] Starting loading');
    authState.user.isLoading.set(true);
    authState.user.error.set(null);
  },

  setError: (error: Error) => {
    logger.debug('[authStore] Setting error', { error });
    authState.user.isLoading.set(false);
    authState.user.error.set(error);
  },

  completeLoading: () => {
    logger.debug('[authStore] Completing loading');
    authState.user.isLoading.set(false);
  },

  // Token actions
  setTokens: (tokens: AuthTokens | null) => {
    logger.debug('[authStore] Setting tokens');
    authState.tokens.data.set(tokens);
  },

  startTokenRefresh: () => {
    logger.debug('[authStore] Starting token refresh');
    authState.tokens.isRefreshing.set(true);
  },

  completeTokenRefresh: () => {
    logger.debug('[authStore] Completing token refresh');
    authState.tokens.isRefreshing.set(false);
  },

  // Status actions
  setStatus: (status: AuthStatus | 'SIGNED_OUT') => {
    logger.debug('[authStore] Setting status', { status });
    authState.status.current.set(status);
  },

  startTransition: () => {
    logger.debug('[authStore] Starting transition');
    authState.status.isTransitioning.set(true);
  },

  completeTransition: () => {
    logger.debug('[authStore] Completing transition');
    authState.status.isTransitioning.set(false);
  },

  reset: () => {
    logger.debug('[authStore] Resetting state');
    authState.set(initialState);
  },

  // Service-connected actions
  login: async (credentials: AuthCredentials): Promise<LoginResult> => {
    try {
      authActions.startLoading();
      authActions.startTransition();

      const result = await authService.login(credentials);

      // Convert service response to client types
      const user = mapServiceUserToClientUser(result.user, result.tokens);

      authActions.setUser(user);
      authActions.setTokens(result.tokens);
      authActions.setStatus(result.status);

      return result;
    } catch (error) {
      logger.error('[authStore] Login failed:', error);
      authActions.setError(error as Error);
      authActions.setStatus('SIGNED_OUT');
      throw error;
    } finally {
      authActions.completeLoading();
      authActions.completeTransition();
    }
  },

  register: async (credentials: RegisterCredentials) => {
    try {
      authActions.startLoading();
      authActions.startTransition();

      const result = await authService.register(credentials);

      // Registration only returns confirmation status
      authActions.setStatus('UNCONFIRMED');

      return result;
    } catch (error) {
      logger.error('[authStore] Registration failed:', error);
      authActions.setError(error as Error);
      authActions.setStatus('SIGNED_OUT');
      throw error;
    } finally {
      authActions.completeLoading();
      authActions.completeTransition();
    }
  },

  refreshToken: async () => {
    const currentToken = authState.tokens.data.get()?.accessToken;
    if (!currentToken) return;

    try {
      authActions.startTokenRefresh();

      const result = await authService.refreshToken(currentToken);

      // Convert service response to client types
      const user = mapServiceUserToClientUser(result.user, result.tokens);

      authActions.setUser(user);
      authActions.setTokens(result.tokens);

      return result;
    } catch (error) {
      logger.error('[authStore] Token refresh failed:', error);
      // On token refresh failure, sign out the user
      authActions.setTokens(null);
      authActions.setUser(null);
      authActions.setStatus('SIGNED_OUT');
      throw error;
    } finally {
      authActions.completeTokenRefresh();
    }
  },

  logout: async () => {
    try {
      authActions.startTransition();
      const session = authState.session.get();
      if (authService.logout) {
        await authService.logout(session);
      }
    } catch (error) {
      logger.error('[authStore] Logout failed:', error);
    } finally {
      // Always clear state on logout attempt
      authActions.setTokens(null);
      authActions.setUser(null);
      authActions.setStatus('SIGNED_OUT');
      authActions.completeTransition();
    }
  },
};

// Selectors
export const authSelectors = {
  getUser: () => authState.user.data.get(),
  isLoading: () => authState.user.isLoading.get(),
  getError: () => authState.user.error.get(),
  getTokens: () => authState.tokens.data.get(),
  isRefreshingToken: () => authState.tokens.isRefreshing.get(),
  getStatus: () => authState.status.current.get(),
  isTransitioning: () => authState.status.isTransitioning.get(),
  getSession: () => authState.session.get(),
};
